<template>
  <div class="container-fluid">
    <!-- Activity Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h2 class="mb-1 fw-bold text-white">Daily Stretch & Movement</h2>
              <p class="mb-0 text-white opacity-75">Let's get moving! Follow along with these fun exercises to stay active.</p>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div class="text-center text-white">
                <div class="fw-bold">{{ totalCaloriesBurned }}</div>
                <small class="opacity-75">Calories</small>
              </div>
              <div class="text-white" style="font-size: 48px;">
                🏃‍♀️
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Routine Selection -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-center gap-3 mb-3">
          <button 
            v-for="routine in routines" 
            :key="routine.id"
            class="btn routine-btn"
            :class="{ active: selectedRoutine?.id === routine.id }"
            @click="selectRoutine(routine)"
          >
            <span class="me-2">{{ routine.icon }}</span>
            {{ routine.name }}
            <small class="d-block opacity-75">{{ routine.totalDuration }}</small>
          </button>
        </div>
        <div class="d-flex justify-content-center gap-3">
          <button 
            v-for="level in levels" 
            :key="level.name"
            class="btn level-btn"
            :class="{ active: selectedLevel === level.name }"
            @click="selectedLevel = level.name"
            :style="{ background: level.color }"
          >
            {{ level.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="row">
      <!-- Video Player Section -->
      <div class="col-lg-8 mb-4">
        <div class="modern-card p-0 overflow-hidden">
          <div class="video-player-container">
            <div class="video-placeholder d-flex align-items-center justify-content-center">
              <div v-if="!isVideoPlaying" class="text-center text-white">
                <button class="play-button mb-3" @click="playVideo">
                  <i class="bi bi-play-fill"></i>
                </button>
                <h5 class="mb-1">{{ currentExercise ? currentExercise.name : 'Select an Exercise' }}</h5>
                <p class="opacity-75">{{ currentExercise ? currentExercise.description : 'Choose an exercise to get started' }}</p>
              </div>
              <div v-else class="text-center text-white">
                <div class="exercise-timer mb-3">
                  <div class="timer-display">{{ formatTime(exerciseTimer) }}</div>
                  <div class="timer-controls">
                    <button class="btn btn-sm btn-light me-2" @click="pauseExercise">
                      <i class="bi bi-pause"></i>
                    </button>
                    <button class="btn btn-sm btn-danger" @click="stopExercise">
                      <i class="bi bi-stop"></i>
                    </button>
                  </div>
                </div>
                <h5>{{ currentExercise.name }}</h5>
                <p class="opacity-75">Keep going! You're doing great!</p>
              </div>
            </div>
          </div>
          
          <!-- Action Buttons -->
          <div class="p-4">
            <div class="row g-2">
              <div class="col-6">
                <button 
                  class="btn w-100" 
                  style="background: #7dd3fc; color: #0c4a6e; font-weight: 600; border-radius: 15px; padding: 15px;"
                  @click="startSelectedExercise"
                  :disabled="!currentExercise"
                >
                  {{ isVideoPlaying ? 'Exercise in Progress...' : 'Start Exercise!' }}
                </button>
              </div>
              <div class="col-6">
                <button 
                  class="btn w-100" 
                  style="background: #f97316; color: white; font-weight: 600; border-radius: 15px; padding: 15px;"
                  @click="showExerciseDetails"
                  :disabled="!currentExercise"
                >
                  📋 View Instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Progress Section -->
      <div class="col-lg-4 mb-4">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Progress Today</h5>
          
          <!-- Circular Progress -->
          <div class="d-flex justify-content-center mb-3">
            <div class="progress-ring">
              <svg width="120" height="120">
                <circle cx="60" cy="60" r="50" stroke="#e5e7eb" stroke-width="8" fill="none"/>
                <circle 
                  cx="60" cy="60" r="50" 
                  stroke="#10b981" 
                  stroke-width="8" 
                  fill="none"
                  :stroke-dasharray="314"
                  :stroke-dashoffset="314 - (314 * progressPercentageToday / 100)"
                  stroke-linecap="round"
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="65" text-anchor="middle" class="progress-text">{{ progressPercentageToday }}%</text>
                <text x="60" y="80" text-anchor="middle" class="progress-label">Done</text>
              </svg>
            </div>
          </div>

          <!-- Exercise Count -->
          <div class="text-center mb-3">
            <h6 class="fw-bold">{{ completedExercisesTodayCount }}/{{ totalExercisesCount }} Exercises Done</h6>
            <small class="text-muted">{{ selectedRoutine ? selectedRoutine.name : 'All Exercises' }}</small>
          </div>

          <!-- Streak Counter -->
          <div class="text-center mb-3">
            <div class="streak-counter p-3 rounded" style="background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);">
              <div class="text-white">
                <div class="fw-bold fs-4">🔥 {{ exerciseStreak }}</div>
                <small>Day Streak</small>
              </div>
            </div>
          </div>

          <!-- Quick Stats -->
          <div class="row g-2">
            <div class="col-6">
              <div class="stat-card text-center p-2 rounded" style="background: var(--bg-secondary);">
                <div class="fw-bold text-primary">{{ todayMinutes }}</div>
                <small class="text-muted">Minutes</small>
              </div>
            </div>
            <div class="col-6">
              <div class="stat-card text-center p-2 rounded" style="background: var(--bg-secondary);">
                <div class="fw-bold text-success">{{ totalCaloriesBurnedToday }}</div>
                <small class="text-muted">Calories</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Exercise Grid Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h4 class="fw-bold">{{ selectedRoutine ? selectedRoutine.name + ' Exercises' : 'All Exercises' }}</h4>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-primary" @click="refreshExercises">
              <i class="bi bi-arrow-clockwise"></i> Refresh
            </button>
            <button class="btn btn-sm btn-primary" @click="resetProgress">
              <i class="bi bi-arrow-counterclockwise"></i> Reset Progress
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading exercises...</span>
      </div>
    </div>

    <div v-else class="row">
      <div v-for="exercise in filteredExercises" :key="exercise.id" class="col-md-6 col-lg-4 mb-4">
        <div 
          class="modern-card exercise-card h-100"
          :class="{ 
            'completed': exercise.completed,
            'selected': currentExercise?.id === exercise.id
          }"
          @click="selectExercise(exercise)"
        >
          <div class="exercise-icon-container d-flex align-items-center justify-content-center" :style="{ background: exercise.color || '#93c5fd' }">
            <div class="exercise-icon">
              {{ exercise.icon }}
            </div>
            <div v-if="exercise.completed" class="completion-badge">
              <i class="bi bi-check-circle-fill"></i>
            </div>
          </div>
          
          <div class="p-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="fw-bold mb-0">{{ exercise.name }}</h6>
              <span class="badge" :style="{ background: getDifficultyColor(exercise.difficulty) }">
                {{ exercise.difficulty }}
              </span>
            </div>
            <p class="text-muted small mb-2">{{ exercise.description }}</p>
            
            <div class="exercise-meta d-flex justify-content-between align-items-center mb-3">
              <small class="text-muted">
                <i class="bi bi-clock me-1"></i>{{ exercise.duration }}
              </small>
              <small class="text-muted">
                <i class="bi bi-fire me-1"></i>{{ exercise.calories }} cal
              </small>
            </div>
            
            <button 
              class="btn btn-start w-100"
              :class="{ 'btn-completed': exercise.completed }"
              @click.stop="startExercise(exercise)"
            >
              <span v-if="exercise.completed">
                <i class="bi bi-check me-1"></i>Completed
              </span>
              <span v-else-if="currentExercise?.id === exercise.id && isVideoPlaying">
                <i class="bi bi-play me-1"></i>In Progress...
              </span>
              <span v-else>
                <i class="bi bi-play me-1"></i>Start
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Exercise Detail Modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click="closeDetailModal">
      <div class="modal-content exercise-detail-modal" @click.stop>
        <div class="modal-header">
          <h5 class="fw-bold">{{ selectedExerciseDetail.name }}</h5>
          <button class="btn-close" @click="closeDetailModal">×</button>
        </div>
        <div class="modal-body">
          <div class="exercise-detail-header mb-4">
            <div class="row">
              <div class="col-md-6">
                <div class="exercise-preview" :style="{ background: selectedExerciseDetail.color || '#93c5fd' }">
                  <div class="exercise-icon-large">{{ selectedExerciseDetail.icon }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <h6 class="fw-bold">Exercise Details</h6>
                <div class="detail-stats">
                  <div class="stat-item">
                    <i class="bi bi-clock text-primary"></i>
                    <span>Duration: {{ selectedExerciseDetail.duration }}</span>
                  </div>
                  <div class="stat-item">
                    <i class="bi bi-fire text-danger"></i>
                    <span>Calories: {{ selectedExerciseDetail.calories }}</span>
                  </div>
                  <div class="stat-item">
                    <i class="bi bi-bar-chart text-success"></i>
                    <span>Level: {{ selectedExerciseDetail.difficulty }}</span>
                  </div>
                  <div class="stat-item">
                    <i class="bi bi-tag text-info"></i>
                    <span>Category: {{ selectedExerciseDetail.category }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="instructions-section">
            <h6 class="fw-bold mb-3">Step-by-Step Instructions</h6>
            <div class="instructions-list">
              <div 
                v-for="(instruction, index) in selectedExerciseDetail.instructions" 
                :key="index"
                class="instruction-item"
              >
                <div class="instruction-number">{{ index + 1 }}</div>
                <div class="instruction-text">{{ instruction }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeDetailModal">Close</button>
          <button class="btn btn-primary" @click="startExerciseFromModal">
            <i class="bi bi-play me-1"></i>Start Exercise
          </button>
        </div>
      </div>
    </div>

    <!-- Achievement Toast -->
    <div v-if="showAchievement" class="achievement-toast">
      <div class="achievement-content">
        <div class="achievement-badge">{{ achievementData.icon }}</div>
        <div>
          <div class="fw-bold">{{ achievementData.title }}</div>
          <div class="small">{{ achievementData.message }}</div>
        </div>
        <button class="btn-close-achievement" @click="showAchievement = false">×</button>
      </div>
    </div>

    <!-- Completion Celebration -->
    <div v-if="showCompletionCelebration" class="completion-celebration">
      <div class="celebration-content">
        <div class="celebration-animation">🎉</div>
        <h4 class="fw-bold text-success">Awesome Work!</h4>
        <p>You completed {{ selectedRoutine ? selectedRoutine.name : 'your exercises' }}!</p>
        <div class="celebration-stats">
          <div class="row">
            <div class="col-4">
              <div class="stat-number">{{ todayMinutes }}</div>
              <div class="stat-label">Minutes</div>
            </div>
            <div class="col-4">
              <div class="stat-number">{{ totalCaloriesBurned }}</div>
              <div class="stat-label">Calories</div>
            </div>
            <div class="col-4">
              <div class="stat-number">{{ completedExercisesCount }}</div>
              <div class="stat-label">Exercises</div>
            </div>
          </div>
        </div>
        <button class="btn btn-primary mt-3" @click="showCompletionCelebration = false">
          Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getRoutines, updateRoutine } from '../services/routineService.js'
import { getExercises } from '../services/exerciseService.js'

export default {
  name: 'ActivityPage',
  data() {
    return {
      loading: true,
      selectedLevel: 'Beginner',
      selectedRoutine: null,
      currentExercise: null,
      selectedExerciseDetail: null,
      showDetailModal: false,
      showAchievement: false,
      showCompletionCelebration: false,
      isVideoPlaying: false,
      exerciseTimer: 0,
      timerInterval: null,
      achievementData: {},
      
      levels: [
        { name: 'Beginner', color: '#86efac' },
        { name: 'Intermediate', color: '#60a5fa' },
        { name: 'Advanced', color: '#fbbf24' }
      ],
      
      exercises: [],
      routines: []
    }
  },
  
  async mounted() {
    await this.loadData();
    this.loadProgress();
  },
  
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  
  computed: {
    filteredExercises() {
      let filtered = this.exercises;
      
      // Filter by selected routine
      if (this.selectedRoutine) {
        filtered = filtered.filter(ex => this.selectedRoutine.exercises.includes(ex.id));
      }
      
      // Filter by difficulty level
      filtered = filtered.filter(ex => ex.difficulty === this.selectedLevel);
      
      return filtered;
    },
    
    completedExercisesTodayCount() {
      const todayIds = new Set(this.getCompletedExercisesToday().map(ex => ex.id))
      return this.filteredExercises.filter(ex => todayIds.has(ex.id)).length
    },

    totalExercisesCount() {
      return this.filteredExercises.length;
    },
    
    progressPercentageToday() {
      if (this.totalExercisesCount === 0) return 0;
      return Math.round((this.completedExercisesTodayCount / this.totalExercisesCount) * 100);
    },
    
    totalCaloriesBurnedToday() {
      const todayCompleted = this.getCompletedExercisesToday()
      return todayCompleted.reduce((total, ex) => total + (ex.calories || 0), 0)
    },
    
    todayMinutes() {
      const completedToday = this.getCompletedExercisesToday();
      return completedToday.reduce((total, ex) => {
        const duration = ex.duration.match(/(\d+)/);
        return total + (duration ? parseInt(duration[1]) : 0);
      }, 0);
    },
    
    exerciseStreak() {
      const streak = JSON.parse(localStorage.getItem('exerciseStreak') || '{"current": 0}');
      return streak.current;
    }
  },
  
  methods: {
    async loadData() {
      try {
        this.loading = true;
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const routinesData = userId ? await getRoutines(userId) : []
        this.routines = routinesData

        // If routines carry numeric exercise IDs, fetch real exercises via API
        const exerciseIdSet = new Set()
        routinesData.forEach(r => {
          (r.exercises || []).forEach(id => {
            if (typeof id === 'number') exerciseIdSet.add(id)
          })
        })

        let fetchedExercises = []
        if (exerciseIdSet.size > 0) {
          fetchedExercises = await getExercises(Array.from(exerciseIdSet))
        } else {
          // Fallback: load all exercises for UI if none specified
          fetchedExercises = await getExercises()
        }

        // Map to UI model and initialize completion state
        const seen = new Map()
        fetchedExercises.forEach(ex => {
          seen.set(ex.id, {
            id: ex.id,
            name: ex.name,
            description: ex.description,
            category: ex.category,
            difficulty: ex.difficulty || 'Beginner',
            duration: ex.duration || '2 minutes',
            calories: ex.calories || 5,
            icon: ex.icon || '🏃',
            color: ex.color || '#93c5fd',
            instructions: ex.instructions || [],
            completed: false
          })
        })
        this.exercises = Array.from(seen.values())
        
        // Set default routine
        if (this.routines.length > 0) {
          this.selectedRoutine = this.routines[0];
        }
        
      } catch (error) {
        console.error('Error loading activity data:', error);
        this.showFeedback('Error loading exercises', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    loadProgress() {
      const savedProgress = JSON.parse(localStorage.getItem('exerciseProgress') || '{}');
      this.exercises.forEach(exercise => {
        if (savedProgress[exercise.id]) {
          exercise.completed = savedProgress[exercise.id].completed;
        }
      });
    },
    
    saveProgress() {
      const progress = {};
      this.exercises.forEach(exercise => {
        progress[exercise.id] = {
          completed: exercise.completed,
          completedAt: exercise.completed ? new Date().toISOString() : null
        };
      });
      localStorage.setItem('exerciseProgress', JSON.stringify(progress));
      
      // Update streak
      this.updateExerciseStreak();
    },
    
    updateExerciseStreak() {
      const today = new Date().toDateString();
      const streak = JSON.parse(localStorage.getItem('exerciseStreak') || '{"days": [], "current": 0}');
      
      if (!streak.days.includes(today)) {
        streak.days.push(today);
        streak.current = this.calculateStreak(streak.days);
        localStorage.setItem('exerciseStreak', JSON.stringify(streak));
      }
    },
    
    calculateStreak(days) {
      if (days.length === 0) return 0;
      
      const sortedDays = days.map(day => new Date(day)).sort((a, b) => b - a);
      let streak = 1;
      
      for (let i = 1; i < sortedDays.length; i++) {
        const diff = (sortedDays[i-1] - sortedDays[i]) / (1000 * 60 * 60 * 24);
        if (diff === 1) {
          streak++;
        } else {
          break;
        }
      }
      
      return streak;
    },
    
    getCompletedExercisesToday() {
      const today = new Date().toDateString();
      const progress = JSON.parse(localStorage.getItem('exerciseProgress') || '{}');
      
      return this.exercises.filter(exercise => {
        const exerciseProgress = progress[exercise.id];
        return exerciseProgress && exerciseProgress.completed && 
               new Date(exerciseProgress.completedAt).toDateString() === today;
      });
    },
    
    selectRoutine(routine) {
      this.selectedRoutine = routine;
      this.currentExercise = null;
    },
    
    selectExercise(exercise) {
      this.currentExercise = exercise;
    },
    
    async startExercise(exercise) {
      if (exercise.completed) return;
      
      this.currentExercise = exercise;
      this.startExerciseTimer(exercise);
    },
    
    startSelectedExercise() {
      if (this.currentExercise && !this.isVideoPlaying) {
        this.startExerciseTimer(this.currentExercise);
      }
    },
    
    startExerciseTimer(exercise) {
      const duration = exercise.duration.match(/(\d+)/);
      const minutes = duration ? parseInt(duration[1]) : 2;
      
      this.exerciseTimer = minutes * 60; // Convert to seconds
      this.isVideoPlaying = true;
      
      this.timerInterval = setInterval(() => {
        this.exerciseTimer--;
        if (this.exerciseTimer <= 0) {
          this.completeExercise(exercise);
        }
      }, 1000);
    },
    
    pauseExercise() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    
    stopExercise() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      this.isVideoPlaying = false;
      this.exerciseTimer = 0;
    },
    
    async completeExercise(exercise) {
      this.stopExercise();
      
      exercise.completed = true;
      this.saveProgress();
      
      // Persist progress to routines API (best-effort)
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        if (userId && this.selectedRoutine?.routine_id) {
          await updateRoutine(userId, this.selectedRoutine.routine_id, {
            last_exercise: this.completedExercisesCount,
            total_exercises: this.totalExercisesCount
          })
        }
      } catch (error) {
        console.error('Error updating routine progress:', error)
      }
      
      // Check for achievements
      this.checkAchievements();
      
      // Check for routine completion
      if (this.selectedRoutine && this.isRoutineCompleted()) {
        setTimeout(() => {
          this.showCompletionCelebration = true;
        }, 1000);
      }
    },
    
    isRoutineCompleted() {
      if (!this.selectedRoutine) return false;
      return this.selectedRoutine.exercises.every(exerciseId => {
        const exercise = this.exercises.find(ex => ex.id === exerciseId);
        return exercise && exercise.completed;
      });
    },
    
    checkAchievements() {
      const completedCount = this.exercises.filter(ex => ex.completed).length;
      
      if (completedCount === 1) {
        this.triggerAchievement({
          icon: '🌟',
          title: 'First Step!',
          message: 'You completed your first exercise!'
        });
      } else if (completedCount === 3) {
        this.triggerAchievement({
          icon: '🏆',
          title: 'Exercise Champion!',
          message: 'You completed 3 exercises!'
        });
      } else if (this.exerciseStreak >= 3) {
        this.triggerAchievement({
          icon: '🔥',
          title: 'Streak Master!',
          message: `${this.exerciseStreak} day exercise streak!`
        });
      }
    },
    
    triggerAchievement(achievement) {
      this.achievementData = achievement;
      this.showAchievement = true;
      setTimeout(() => {
        this.showAchievement = false;
      }, 5000);
    },
    
    showExerciseDetails() {
      if (this.currentExercise) {
        this.selectedExerciseDetail = this.currentExercise;
        this.showDetailModal = true;
      }
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedExerciseDetail = null;
    },
    
    startExerciseFromModal() {
      this.closeDetailModal();
      if (this.selectedExerciseDetail) {
        this.startExercise(this.selectedExerciseDetail);
      }
    },
    
    playVideo() {
      if (this.currentExercise) {
        this.startExerciseTimer(this.currentExercise);
      }
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    
    getDifficultyColor(difficulty) {
      switch (difficulty) {
        case 'Beginner': return '#86efac';
        case 'Intermediate': return '#60a5fa';
        case 'Advanced': return '#fbbf24';
        default: return '#93c5fd';
      }
    },
    
    async refreshExercises() {
      await this.loadData();
      this.showFeedback('Exercises refreshed!', 'success');
    },
    
    resetProgress() {
      if (confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
        this.exercises.forEach(exercise => {
          exercise.completed = false;
        });
        localStorage.removeItem('exerciseProgress');
        localStorage.removeItem('exerciseStreak');
        this.showFeedback('Progress reset successfully!', 'success');
      }
    },
    
    showFeedback(message, type) {
      const alertClass = type === 'success' ? 'alert-success' : 'alert-danger';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 350px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      
      document.body.appendChild(alert);
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 4000);
    }
  }
}
</script>

<style scoped>
.routine-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 15px 20px;
  border-radius: 15px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 180px;
  text-align: center;
}

.routine-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--role-primary);
  transform: translateY(-2px);
}

.routine-btn.active {
  background: var(--role-primary);
  color: white;
  border-color: var(--role-primary);
}

.level-btn {
  color: white;
  border: none;
  border-radius: 25px;
  padding: 8px 24px;
  font-weight: 600;
  transition: all 0.3s ease;
  opacity: 0.7;
}

.level-btn.active {
  opacity: 1;
  transform: scale(1.05);
}

.video-player-container {
  height: 350px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

.video-placeholder {
  height: 100%;
  color: white;
}

.play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 3px solid white;
  color: white;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.play-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.exercise-timer {
  text-align: center;
}

.timer-display {
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 15px;
}

.timer-controls {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.progress-ring {
  position: relative;
}

.progress-text {
  font-size: 18px;
  font-weight: bold;
  fill: #10b981;
}

.progress-label {
  font-size: 12px;
  fill: var(--text-secondary);
}

.streak-counter {
  transition: all 0.3s ease;
}

.streak-counter:hover {
  transform: scale(1.05);
}

.exercise-card {
  transition: all 0.3s ease;
  border: none;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  position: relative;
}

.exercise-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.exercise-card.completed {
  border: 2px solid #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.exercise-card.selected {
  border: 2px solid var(--role-primary);
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.3);
}

.exercise-icon-container {
  height: 80px;
  border-radius: 20px 20px 0 0;
  position: relative;
}

.exercise-icon {
  font-size: 32px;
}

.completion-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #10b981;
  font-size: 24px;
  background: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exercise-meta {
  border-top: 1px solid var(--border-color);
  padding-top: 10px;
}

.btn-start {
  background: #f87171;
  color: white;
  border: none;
  border-radius: 15px;
  padding: 10px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-start:hover {
  background: #ef4444;
  transform: translateY(-2px);
}

.btn-completed {
  background: #10b981 !important;
  color: white;
}

.btn-completed:hover {
  background: #059669 !important;
}

/* Exercise Detail Modal */
.exercise-detail-modal {
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.exercise-preview {
  height: 200px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.exercise-icon-large {
  font-size: 80px;
  color: white;
}

.detail-stats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.instructions-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.instruction-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 10px;
}

.instruction-number {
  background: var(--role-primary);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.instruction-text {
  flex: 1;
  line-height: 1.5;
}

/* Achievement Toast */
.achievement-toast {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  animation: slideIn 0.5s ease;
}

.achievement-content {
  background: var(--bg-primary);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 15px;
  max-width: 350px;
  color: var(--text-primary);
  border: 2px solid #10b981;
}

.achievement-badge {
  font-size: 32px;
}

.btn-close-achievement {
  background: none;
  border: none;
  font-size: 20px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Completion Celebration */
.completion-celebration {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.celebration-content {
  background: var(--bg-primary);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  max-width: 500px;
  margin: 20px;
}

.celebration-animation {
  font-size: 80px;
  margin-bottom: 20px;
  animation: bounce 1s infinite;
}

.celebration-stats {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: var(--role-primary);
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .routine-btn {
    min-width: 140px;
    padding: 12px 15px;
  }
  
  .video-player-container {
    height: 250px;
  }
  
  .timer-display {
    font-size: 36px;
  }
  
  .exercise-detail-modal {
    margin: 10px;
    max-height: 90vh;
  }
  
  .celebration-content {
    padding: 30px 20px;
  }
  
  .celebration-animation {
    font-size: 60px;
  }
}
</style> 